/*
	This extension was copied and adapted from the @tiptap-pro/extension-unique-id package.
	In summary, I've done the following:
		* replaced the generation of uuids with the retrieval of the user's personId
		* changed the name of the extension to creator-attribute
		* changed the name of the attribute
*/

import {
	Extension,
	findChildren,
	combineTransactionSteps,
	getChangedRanges,
	findChildrenInRange
} from "@tiptap/core";
import { Plugin, PluginKey } from "prosemirror-state";
import { Slice, Fragment } from "prosemirror-model";

export default Extension.create( {
	name: "creator-attribute",
	// we’ll set a very high priority to make sure this runs first
	// and is compatible with `appendTransaction` hooks of other extensions
	priority: 10000,
	addOptions() {
		return {
			attributeName: "creator",
			types: [
				"teaming-commitment",
				"teaming-topic",
				"teaming-decision",
			],
			getPersonId: () => undefined,
			filterTransaction: null,
		};
	},
	addGlobalAttributes() {
		return [
			{
				types: this.options.types,
				attributes: {
					[ this.options.attributeName ]: {
						default: null,
						keepOnSplit: false,
						parseHTML: element => element.getAttribute( `data-${ this.options.attributeName }` ),
						renderHTML: attributes => {
							if ( !attributes[ this.options.attributeName ] ) {
								return {};
							}
							return {
								[ `data-${ this.options.attributeName }` ]: attributes[ this.options.attributeName ],
							};
						},
					},
				},
			},
		];
	},
	// check initial content for missing creator data
	onCreate() {
		const { view, state, } = this.editor;
		const { tr, doc, } = state;
		const { types, attributeName, getPersonId, } = this.options;
		const nodesWithoutCreator = findChildren( doc, node => {
			return types.includes( node.type.name ) &&
				node.attrs[ attributeName ] === null;
		} );
		nodesWithoutCreator.forEach( ( { node, pos, } ) => {
			tr.setNodeMarkup( pos, undefined, {
				...node.attrs,
				[ attributeName ]: getPersonId(),
			} );
		} );
		view.dispatch( tr );
	},
	addProseMirrorPlugins() {
		let dragSourceElement = null;
		let transformPasted = false;
		return [
			new Plugin( {
				key: new PluginKey( "creator-attribute" ),
				appendTransaction: ( transactions, oldState, newState ) => {
					const docChanges = transactions.some( transaction => transaction.docChanged ) &&
						!oldState.doc.eq( newState.doc );
					const filterTransactions = this.options.filterTransaction &&
						transactions.some( tr => {
							let _a, _b;
							// eslint-disable-next-line no-cond-assign
							return !( ( _b = ( _a = this.options ).filterTransaction ) === null ||
								// eslint-disable-next-line no-void
								_b === void 0 ? void 0 : _b.call( _a, tr ) );
						} );
					if ( !docChanges || filterTransactions ) {
						return;
					}
					const { tr, } = newState;
					const { types, attributeName, getPersonId, } = this.options;
					const transform = combineTransactionSteps( oldState.doc, transactions );
					// get changed ranges based on the old state
					const changes = getChangedRanges( transform );
					changes.forEach( ( { newRange, } ) => {
						const newNodes = findChildrenInRange( newState.doc, newRange, node => {
							return types.includes( node.type.name );
						} );
						newNodes.forEach( ( { node, pos, } ) => {
							let _a;
							// instead of checking `node.attrs[attributeName]` directly
							// we look at the current state of the node within `tr.doc`.
							// this helps to prevent adding new creator data to the same
							// node if the node changed multiple times within one transaction
							// eslint-disable-next-line no-cond-assign, no-void
							const dateVal = ( _a = tr.doc.nodeAt( pos ) ) === null || _a === void 0 ? void 0 : _a.attrs[ attributeName ];
							if ( dateVal === null ) {
								tr.setNodeMarkup( pos, undefined, {
									...node.attrs,
									[ attributeName ]: getPersonId(),
								} );
								return;
							}
						} );
					} );
					if ( !tr.steps.length ) {
						return;
					}
					return tr;
				},
				// we register a global drag handler to track the current drag source element
				view( view ) {
					const handleDragstart = event => {
						let _a;
						// eslint-disable-next-line no-cond-assign, no-void
						dragSourceElement = ( ( _a = view.dom.parentElement ) === null || _a === void 0 ? void 0 : _a.contains( event.target ) ) ?
							view.dom.parentElement :
							null;
					};
					window.addEventListener( "dragstart", handleDragstart );
					return {
						destroy() {
							window.removeEventListener( "dragstart", handleDragstart );
						},
					};
				},
				props: {
					// `handleDOMEvents` is called before `transformPasted`
					// so we can do some checks before
					handleDOMEvents: {
						// only create new creator data for dropped content while holding `alt`
						// or content is dragged from another editor
						drop: ( view, event ) => {
							let _a;
							if ( dragSourceElement !== view.dom.parentElement ||
								// eslint-disable-next-line no-cond-assign, no-void
								( ( _a = event.dataTransfer ) === null || _a === void 0 ? void 0 : _a.effectAllowed ) === "copy" ) {
								dragSourceElement = null;
								transformPasted = true;
							}
							return false;
						},
						// always create new creator data on pasted content
						paste: () => {
							transformPasted = true;
							return false;
						},
					},
					// we’ll remove creator data for every pasted node
					// so we can create a new one within `appendTransaction`
					transformPasted: slice => {
						if ( !transformPasted ) {
							return slice;
						}
						const { types, attributeName, } = this.options;
						const removeCreator = fragment => {
							const list = [];
							fragment.forEach( node => {
								// don’t touch text nodes
								if ( node.isText ) {
									list.push( node );
									return;
								}
								// check for any other child nodes
								if ( !types.includes( node.type.name ) ) {
									list.push( node.copy( removeCreator( node.content ) ) );
									return;
								}
								// remove creator data
								const nodeWithoutCreator = node.type.create( {
									...node.attrs,
									[ attributeName ]: null,
								}, removeCreator( node.content ), node.marks );
								list.push( nodeWithoutCreator );
							} );
							return Fragment.from( list );
						};
						// reset check
						transformPasted = false;
						return new Slice( removeCreator( slice.content ), slice.openStart, slice.openEnd );
					},
				},
			} ),
		];
	},
} );
